import { Alert, Button, Card, Input, Space, Typography } from 'antd'
import { useState, type JSX } from 'react'
import { parse as parseDomain } from 'tldts'

const BLOCKED_ROOT_DOMAIN = 'community.com'

export function isValidCustomDomain(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  const result = parseDomain(trimmed)
  if (result.isIp || result.hostname === null || result.domain === null) return false
  if (!result.subdomain) return false
  if (result.domain === BLOCKED_ROOT_DOMAIN) return false
  return true
}

type AddDomainFormProps = {
  canWrite: boolean
  domainInput: string
  isAdding: boolean
  whiteLabelEnabled: boolean
  onDomainInputChange: (value: string) => void
  onSubmit: () => void
}

export function AddDomainForm({
  canWrite,
  domainInput,
  isAdding,
  whiteLabelEnabled,
  onDomainInputChange,
  onSubmit,
}: AddDomainFormProps): JSX.Element {
  const [isTouched, setIsTouched] = useState(false)

  if (!whiteLabelEnabled) {
    return (
      <Alert
        description="Enable white-labeling on the General tab before adding a custom domain."
        message="White-labeling Required"
        showIcon
        type="warning"
      />
    )
  }

  const trimmed = domainInput.trim()
  const isInvalid = isTouched && trimmed.length > 0 && !isValidCustomDomain(trimmed)
  const canSubmit = canWrite && isValidCustomDomain(trimmed)

  return (
    <Card title="Add Custom Domain">
      <Typography.Text style={{ display: 'block', marginBottom: '8px' }} type="secondary">
        Domain name
      </Typography.Text>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          disabled={!canWrite}
          onBlur={() => setIsTouched(true)}
          onChange={(event) => onDomainInputChange(event.target.value)}
          onPressEnter={() => canSubmit && onSubmit()}
          placeholder="sms.example.com"
          size="large"
          status={isInvalid ? 'error' : undefined}
          value={domainInput}
        />
        <Button disabled={!canSubmit} loading={isAdding} onClick={onSubmit} size="large" type="primary">
          Add Domain
        </Button>
      </Space.Compact>
      {isInvalid ? (
        <Typography.Text style={{ display: 'block', marginTop: '8px', fontSize: 13 }} type="danger">
          Enter a subdomain (e.g. sms.example.com). Root domains and community.com domains are not allowed. IP addresses
          are not allowed.
        </Typography.Text>
      ) : (
        <Typography.Text style={{ display: 'block', marginTop: '12px', fontSize: 13 }} type="secondary">
          DNS records will be generated automatically. Publish them at your provider and click Verify Now to activate.
        </Typography.Text>
      )}
    </Card>
  )
}
